// Calendar Month View Component (VAC-8)
// Traditional calendar grid showing vacation periods

import React, { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  parseISO
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarDateCell } from './CalendarDateCell'
import type { CalendarEntry } from '@/types/vacation'

interface CalendarMonthViewProps {
  currentMonth: Date
  vacationEntries: CalendarEntry[]
  onMonthChange: (newMonth: Date) => void
  onDateClick?: (date: Date) => void
  isLoading?: boolean
}

export const CalendarMonthView: React.FC<CalendarMonthViewProps> = ({
  currentMonth,
  vacationEntries,
  onMonthChange,
  onDateClick,
  isLoading = false
}) => {
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)

  // Get the full week range to display (includes days from prev/next month)
  const calendarStart = startOfWeek(monthStart, { locale: ptBR })
  const calendarEnd = endOfWeek(monthEnd, { locale: ptBR })
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

  // Group vacations by date for efficient lookup
  const vacationsByDate = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>()

    vacationEntries.forEach(entry => {
      // Fix timezone: use parseISO instead of new Date() to avoid timezone issues
      const entryStart = parseISO(entry.startDate)
      const entryEnd = parseISO(entry.endDate)
      const entryDays = eachDayOfInterval({ start: entryStart, end: entryEnd })

      entryDays.forEach(day => {
        const dateKey = format(day, 'yyyy-MM-dd')
        if (!map.has(dateKey)) {
          map.set(dateKey, [])
        }
        map.get(dateKey)!.push(entry)
      })
    })

    return map
  }, [vacationEntries])

  const handlePrevMonth = () => {
    onMonthChange(subMonths(currentMonth, 1))
  }

  const handleNextMonth = () => {
    onMonthChange(addMonths(currentMonth, 1))
  }

  const handleToday = () => {
    onMonthChange(new Date())
  }

  return (
    <div>
      {/* Header with Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevMonth}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Mês anterior"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white capitalize min-w-[200px] text-center">
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
          </h2>
          <button
            onClick={handleNextMonth}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Próximo mês"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        <button
          onClick={handleToday}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Hoje
        </button>
      </div>

      {/* Week Day Headers */}
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-1">
        {weekDays.map(day => (
          <div key={day} className="text-center py-2">
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
              {day}
            </span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {days.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd')
          const vacations = vacationsByDate.get(dateKey) || []
          const isCurrentMonth = isSameMonth(day, currentMonth)

          return (
            <CalendarDateCell
              key={dateKey}
              date={day}
              vacations={vacations}
              isCurrentMonth={isCurrentMonth}
              onClick={onDateClick}
            />
          )
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500 dark:bg-green-600" />
          <span className="text-gray-700 dark:text-gray-300">Aprovada</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500 dark:bg-yellow-600" />
          <span className="text-gray-700 dark:text-gray-300">Pendente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded border-2 border-blue-500 dark:border-blue-400" />
          <span className="text-gray-700 dark:text-gray-300">Hoje</span>
        </div>
      </div>
    </div>
  )
}
